import type {
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import {
	MutationCache,
	type MutationObserver,
	QueryCache,
	QueryClient,
} from "@tanstack/react-query";
import type { ApiError } from "./fetching";
import { reportAppError } from "./telemetry";

type ArgsFor<T> = [T] extends [] | [undefined] ? [] : [args?: T];

export function createReactQueryKeys<T extends Record<string, any>>(
	baseKey: string,
	queryNames: (keyof T)[],
): {
	[K in keyof T]: (...args: ArgsFor<T[K]>) => string[];
} {
	const result = {} as {
		[K in keyof T]: (...args: ArgsFor<T[K]>) => string[];
	};

	for (const name of queryNames) {
		result[name] = ((...args: any[]) => {
			const suffix = args[0] !== undefined ? JSON.stringify(args[0]) : null;
			return suffix ? [baseKey, String(name), suffix] : [baseKey, String(name)];
		}) as (...args: ArgsFor<T[typeof name]>) => string[];
	}

	return result;
}

export type CustomQueryHook<
	TResponse = unknown,
	TParams = unknown,
	TTransformedData = TResponse,
	TError = ApiError,
> = TParams extends NonNullable<TParams>
	? (
			params: TParams,
			options?: Omit<
				UseQueryOptions<TResponse, TError, TTransformedData>,
				"queryKey"
			>,
		) => UseQueryResult<TTransformedData, TError>
	: (
			options?: Omit<
				UseQueryOptions<TResponse, TError, TTransformedData>,
				"queryKey"
			>,
		) => UseQueryResult<TTransformedData, TError>;

export type CustomMutationHook<
	TParams = unknown,
	TResponse = unknown,
	TError = ApiError,
	TContext = unknown,
> = (
	options?: UseMutationOptions<TResponse, TError, TParams, TContext>,
) => UseMutationResult<TResponse, TError, TParams>;

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error) => reportAppError(error, "query"),
	}),
	mutationCache: new MutationCache({
		onError: (error) => reportAppError(error, "mutation"),
	}),
	defaultOptions: {
		queries: {
			retry: 0,
			staleTime: 1 * 60 * 1000,
		},
		mutations: {
			retry: 0,
		},
	},
});

const SENSITIVE_NO_CACHE_QUERY_DEFAULTS = {
	staleTime: 0,
	gcTime: 0,
	refetchOnMount: "always" as const,
	refetchOnWindowFocus: true,
	refetchOnReconnect: true,
};

queryClient.setQueryDefaults(["payments"], SENSITIVE_NO_CACHE_QUERY_DEFAULTS);
queryClient.setQueryDefaults(
	["payment_operators"],
	SENSITIVE_NO_CACHE_QUERY_DEFAULTS,
);

export function waitForMutation<TData>(
	mutationObserver: MutationObserver<TData>,
): Promise<TData> {
	return new Promise((resolve, reject) => {
		mutationObserver.subscribe((res) => {
			if (res.isError) {
				reject(res.error);
				return;
			}
			if (res.isSuccess) {
				resolve(res.data);
				return;
			}
		});
	});
}

// Utility function for invalidating queries
export function invalidateQueries(queryKey: string[]) {
	queryClient.invalidateQueries({ queryKey });
}
